/**
 * How a slideshow page shows its text, stored as `page.slideshow`.
 * The defaults are what slideshows did before the settings existed.
 */

export const SLIDESHOW_INTRO = Object.freeze(['auto', 'always', 'never']);
export const SLIDESHOW_DESKTOP_PLACEMENTS = Object.freeze(['panel', 'corner', 'none']);
export const SLIDESHOW_PHONE_PLACEMENTS = Object.freeze(['first', 'last', 'hidden']);
export const SLIDESHOW_PHONE_ALIGN = Object.freeze(['center', 'top']);
/** The theme type stacks the text can take: body (as written) or heading. */
export const SLIDESHOW_TYPES = Object.freeze(['body', 'heading']);
/** Where the spread sits in the viewport: a 3×3 anchor, rows top/middle/bottom. */
export const SLIDESHOW_SPREAD_ALIGN = Object.freeze(['tl', 'tc', 'tr', 'ml', 'mc', 'mr', 'bl', 'bc', 'br']);
/** Screen corners the logo, close button and counter can take, or hidden. */
export const SLIDESHOW_CHROME_CORNERS = Object.freeze(['tl', 'tr', 'bl', 'br', 'hidden']);
export const SLIDESHOW_CHROME_ITEMS = Object.freeze(['logo', 'close', 'counter']);
/** Widest and tallest spread (width / height) a manual ratio may ask for. */
export const SPREAD_RATIO_LIMITS = Object.freeze({ min: 0.25, max: 6 });

/** Plain-text length past which `intro: 'auto'` opens on a full-screen intro. */
export const LONG_TEXT_THRESHOLD = 800;

export const SLIDESHOW_DEFAULTS = Object.freeze({
    intro: 'auto',
    desktop: Object.freeze({
        placement: 'panel',
        width: Object.freeze({ min: 35, max: 65 }),
        backdrop: 10,
    }),
    phone: Object.freeze({ placement: 'first', align: 'center' }),
    type: 'body',
    // ratio null: the spread takes its shape from the images.
    // fill: the spread takes the screen's shape instead, so ratio and align don't apply.
    spread: Object.freeze({ ratio: null, align: 'mc', fill: false }),
    // Phone values of null follow the desktop corner.
    chrome: Object.freeze({
        logo: 'tl',
        close: 'tr',
        counter: 'br',
        phone: Object.freeze({ logo: null, close: null, counter: null }),
    }),
});

/** Spread shape the editor's image tiles use for a fill-screen slideshow: a typical wide screen. */
export const FILL_TILE_RATIO = 16 / 10;

const WIDTH_LIMITS = { min: 15, max: 120 };

function pick(value, allowed, fallback) {
    return allowed.includes(value) ? value : fallback;
}

function clampNumber(value, min, max, fallback) {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(min, Math.min(max, Math.round(n)));
}

const roundSide = (value) => Math.round(value * 100) / 100;

/**
 * A manual spread shape as { width, height } (e.g. 3 × 2), or null for one taken
 * from the images. Sides are rounded to hundredths; a shape outside the limits is dropped.
 */
export function normalizeSpreadRatio(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const width = roundSide(Number(value.width));
    const height = roundSide(Number(value.height));
    if (!(width > 0 && height > 0 && width <= 10000 && height <= 10000)) return null;
    const ratio = width / height;
    if (ratio < SPREAD_RATIO_LIMITS.min || ratio > SPREAD_RATIO_LIMITS.max) return null;
    return { width, height };
}

/** The spread's width / height from the settings, or null when the images decide. */
export function spreadRatioOf(settings) {
    const ratio = normalizeSlideshowSettings(settings).spread.ratio;
    return ratio ? ratio.width / ratio.height : null;
}

/** The spread shape for the editor's image tiles, where there is no screen to fill. */
export function tileSpreadRatioOf(settings) {
    return normalizeSlideshowSettings(settings).spread.fill ? FILL_TILE_RATIO : spreadRatioOf(settings);
}

/** A complete, clamped settings object; anything missing or invalid takes the default. */
export function normalizeSlideshowSettings(value) {
    const raw = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    const desktop = raw.desktop && typeof raw.desktop === 'object' ? raw.desktop : {};
    const width = desktop.width && typeof desktop.width === 'object' ? desktop.width : {};
    const phone = raw.phone && typeof raw.phone === 'object' ? raw.phone : {};
    const spread = raw.spread && typeof raw.spread === 'object' ? raw.spread : {};
    const chrome = raw.chrome && typeof raw.chrome === 'object' && !Array.isArray(raw.chrome) ? raw.chrome : {};
    const chromePhone = chrome.phone && typeof chrome.phone === 'object' ? chrome.phone : {};
    const defaults = SLIDESHOW_DEFAULTS;

    const min = clampNumber(width.min, WIDTH_LIMITS.min, WIDTH_LIMITS.max, defaults.desktop.width.min);
    const max = clampNumber(width.max, WIDTH_LIMITS.min, WIDTH_LIMITS.max, defaults.desktop.width.max);

    return {
        intro: pick(raw.intro, SLIDESHOW_INTRO, defaults.intro),
        desktop: {
            placement: pick(desktop.placement, SLIDESHOW_DESKTOP_PLACEMENTS, defaults.desktop.placement),
            width: { min: Math.min(min, max), max: Math.max(min, max) },
            backdrop: clampNumber(desktop.backdrop, 0, 100, defaults.desktop.backdrop),
        },
        phone: {
            placement: pick(phone.placement, SLIDESHOW_PHONE_PLACEMENTS, defaults.phone.placement),
            align: pick(phone.align, SLIDESHOW_PHONE_ALIGN, defaults.phone.align),
        },
        type: pick(raw.type, SLIDESHOW_TYPES, defaults.type),
        spread: {
            ratio: normalizeSpreadRatio(spread.ratio),
            align: pick(spread.align, SLIDESHOW_SPREAD_ALIGN, defaults.spread.align),
            fill: spread.fill === true,
        },
        chrome: {
            ...Object.fromEntries(SLIDESHOW_CHROME_ITEMS.map((item) => (
                [item, pick(chrome[item], SLIDESHOW_CHROME_CORNERS, defaults.chrome[item])]
            ))),
            phone: Object.fromEntries(SLIDESHOW_CHROME_ITEMS.map((item) => (
                [item, pick(chromePhone[item], SLIDESHOW_CHROME_CORNERS, null)]
            ))),
        },
    };
}

/**
 * Where the logo, close button and counter sit on this device:
 * { logo, close, counter }, each a corner or 'hidden'.
 */
export function slideshowChromeFor(settings, { phone = false } = {}) {
    const { chrome } = normalizeSlideshowSettings(settings);
    return Object.fromEntries(SLIDESHOW_CHROME_ITEMS.map((item) => (
        [item, (phone && chrome.phone[item]) || chrome[item]]
    )));
}

/** Plain text of some HTML, for the intro length rule. */
export function plainTextLength(html) {
    return String(html || '').replace(/<[^>]*>/g, '').trim().length;
}

/**
 * Whether the slideshow opens on its full-screen intro.
 * `auto` keeps the original rule: long text only.
 */
export function wantsIntro(settings, textLength) {
    const intro = normalizeSlideshowSettings(settings).intro;
    if (intro === 'always') return true;
    if (intro === 'never') return false;
    return textLength > LONG_TEXT_THRESHOLD;
}

/** Where the text slide sits among the phone slides, or null for none. */
export function phoneTextSlot(settings) {
    const placement = normalizeSlideshowSettings(settings).phone.placement;
    return placement === 'hidden' ? null : placement;
}
